import React from 'react';

import classNames from 'classnames';

import { selectClassName, SELECT_BACKGROUND } from '../FormField';

import SelectField from 'CommonComponents/SelectField';
import ToggleInput from 'CommonComponents/ToggleInput';

/**
 * The controls the section options panels are built from.
 */
export const Group = ({ title, children }) => (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-py-3 tw-border-b tw-border-charcoal-15 last:tw-border-b-0">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-charcoal-75">{title}</span>
        {children}
    </div>
);

/**
 * An on/off option, with an optional line of help under its label.
 */
export const Toggle = ({ label, hint, checked, disabled, onChange }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <span className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-text-charcoal">{label}</span>
            {hint && <span className="tw-text-xs tw-text-charcoal-60">{hint}</span>}
        </span>
        <ToggleInput label={<span className="tw-sr-only">{label}</span>} noBottomSpacing checked={checked} disabled={disabled} onChange={onChange} />
    </div>
);

export const Choice = ({ id, label, value, choices, disabled, onChange }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <label className="tw-text-sm tw-text-charcoal tw-mb-0" htmlFor={id}>
            {label}
        </label>
        <div className="tw-w-44 tw-shrink-0">
            <SelectField
                inputId={id}
                className={selectClassName}
                backgroundColor={SELECT_BACKGROUND}
                options={choices}
                value={value}
                isDisabled={disabled}
                onChange={(option) => option && onChange(option.value)}
            />
        </div>
    </div>
);

/**
 * A row of radio buttons choosing one value.
 */
export const RadioChoice = ({ name, label, value, choices, disabled, onChange }) => (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2" role="radiogroup" aria-label={label}>
        {choices.map((choice) => (
            <label key={choice.value} className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50 tw-mb-0">
                <input type="radio" name={name} checked={value === choice.value} disabled={disabled} onChange={() => onChange(choice.value)} />
                {choice.label}
            </label>
        ))}
    </div>
);

export const OptionHeading = ({ id, children }) => (
    <h4 id={id} className="tw-text-2xs tw-font-semibold tw-uppercase tw-text-blue tw-m-0">
        {children}
    </h4>
);

/**
 * A set of on/off chips. A missing value means every choice is shown.
 */
export const ChoicesOption = ({ sectionKey, definition, value, disabled, onChange }) => {
    const all = definition.choices.map((choice) => choice.value);
    const selected = Array.isArray(value) ? value : all;
    const headingId = `mp-${sectionKey}-${definition.key}`;

    const toggle = (choice) => {
        const next = selected.includes(choice) ? selected.filter((item) => item !== choice) : [...selected, choice];

        onChange(all.filter((item) => next.includes(item)));
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <OptionHeading id={headingId}>{definition.label}</OptionHeading>
            <div className="tw-flex tw-flex-wrap tw-gap-1.5" role="group" aria-labelledby={headingId}>
                {definition.choices.map((choice) => {
                    const pressed = selected.includes(choice.value);

                    return (
                        <button
                            key={choice.value}
                            type="button"
                            aria-pressed={pressed}
                            disabled={disabled}
                            onClick={() => toggle(choice.value)}
                            className={classNames(
                                'tw-rounded-lg tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
                                pressed ? 'tw-bg-blue tw-border-blue tw-text-white' : 'tw-bg-white tw-border-charcoal-15 tw-text-charcoal-75',
                            )}
                        >
                            {choice.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export const ToggleOption = ({ definition, value, disabled, onChange }) => (
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
            <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{definition.label}</span>
            {definition.help && <span className="tw-text-xs tw-text-charcoal-75">{definition.help}</span>}
        </div>
        <ToggleInput
            label={<span className="tw-sr-only">{definition.label}</span>}
            noBottomSpacing
            checked={value ?? true}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
        />
    </div>
);
